/**
 * GameAnalytics - Tufte-styled player analytics with premium gating.
 *
 * Uses PlayerStatsTable components for compact, data-dense display.
 * Preserves: premium gating, player detail modal, position filtering.
 *
 * @param {Object} game - Game data with players analytics
 * @param {string} sport - Sport type (nba, nhl, nfl)
 */
import { useState, useCallback } from 'react';
import Card from '../common/Card';
import { PlayerStatsTable } from './tufte';
import PlayerDetailModal from '../streaks/PlayerDetailModal';
import PremiumGateModal from '../common/PremiumGateModal';
import playerDetailsService from '../../services/playerDetailsService';
import { useAuth } from '../../contexts/AuthContext';

const GameAnalytics = ({ game, sport = 'nba' }) => {
  const { userProfile } = useAuth();
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerDetails, setPlayerDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [hasDetailsError, setHasDetailsError] = useState(false);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const hasPremiumAccess = userProfile?.subscription_tier === 'premium' ||
                           userProfile?.subscription_tier === 'pro' ||
                           userProfile?.is_admin;

  const handlePlayerClick = useCallback(async (player) => {
    if (!player.id) return;

    if (!hasPremiumAccess) {
      setShowPremiumModal(true);
      return;
    }

    setSelectedPlayer(player);
    setPlayerDetails(null);
    setIsLoadingDetails(true);
    setHasDetailsError(false);
    setShowPlayerModal(true);

    try {
      const details = await playerDetailsService.getPlayerDetails(sport, player.id, {
        gamesLimit: 10,
        includePredictions: false,
      });
      setPlayerDetails(details);
    } catch (error) {
      console.error('Error fetching player details:', error);
      setHasDetailsError(true);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [sport, hasPremiumAccess]);

  const handleClosePlayerModal = useCallback(() => {
    setShowPlayerModal(false);
    setSelectedPlayer(null);
    setPlayerDetails(null);
    setHasDetailsError(false);
  }, []);

  if (!game) {
    return (
      <Card className="p-8 text-center">
        <p className="text-text-secondary">No analytics data available</p>
      </Card>
    );
  }

  const players = game.players || [];
  const awayTeamId = game.game?.away_team?.id || game.away_team?.id;
  const homeTeamId = game.game?.home_team?.id || game.home_team?.id;

  // Get unique positions
  const positions = ['all', ...new Set(
    players.map(p => p.player?.position).filter(Boolean)
  )];

  // Filter by position
  const filtered = selectedPosition === 'all'
    ? players
    : players.filter(p => p.player?.position === selectedPosition);

  // Split by team
  const awayPlayers = filtered.filter(p => p.player?.team_id === awayTeamId);
  const homePlayers = filtered.filter(p => p.player?.team_id === homeTeamId);

  const awayTeam = game.game?.away_team || game.away_team || {};
  const homeTeam = game.game?.home_team || game.home_team || {};

  return (
    <div className="space-y-4">
      {/* Position filter pills */}
      {positions.length > 2 && (
        <div className="flex gap-1.5 flex-wrap">
          {positions.map(pos => (
            <button
              key={pos}
              onClick={() => setSelectedPosition(pos)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                selectedPosition === pos
                  ? 'bg-accent-blue text-white'
                  : 'bg-bg-secondary text-text-secondary hover:text-text-primary'
              }`}
            >
              {pos === 'all' ? 'All' : pos}
            </button>
          ))}
        </div>
      )}

      {/* Player tables side by side */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-text-secondary">
            No player analytics available{selectedPosition !== 'all' ? ` for ${selectedPosition}` : ''}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PlayerStatsTable
            players={awayPlayers}
            team={awayTeam}
            isHome={false}
            onPlayerClick={handlePlayerClick}
            sport={sport}
          />
          <PlayerStatsTable
            players={homePlayers}
            team={homeTeam}
            isHome={true}
            onPlayerClick={handlePlayerClick}
            sport={sport}
          />
        </div>
      )}

      {/* Player Detail Modal */}
      <PlayerDetailModal
        isOpen={showPlayerModal}
        onClose={handleClosePlayerModal}
        player={selectedPlayer}
        playerDetails={playerDetails}
        isLoading={isLoadingDetails}
        hasError={hasDetailsError}
        sport={sport}
        streakData={selectedPlayer}
      />

      {/* Premium Gate Modal */}
      <PremiumGateModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        title="Premium Player Analysis"
        description="Unlock detailed player statistics, performance trends, and betting insights with a premium subscription."
        features={[
          "Detailed player statistics and averages",
          "Recent game performance charts",
          "Home/Away split analysis",
          "Active streaks and hit rates"
        ]}
      />
    </div>
  );
};

export default GameAnalytics;
